// Copyright ©️ 2026 eVolve MEP, LLC

import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

// oxlint-disable-next-line no-restricted-imports
import type { Modal } from '@mantine/core';
import { v4 as uuid } from 'uuid';

import { type ModalId, useModalStack } from 'components/Mantine/Modals/useModalStack';
import type { WithRequiredNotNull } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { DEFAULT_TRANSITIONS_DURATION } from 'styles/theme/useGenerateMantineTheme';

/** Pass these down to your {@link WrappedModal}. */
export type ModalRegistryManagedProps = WithRequiredNotNull<Pick<Modal.Props, 'opened' | 'onClose' | 'keepMounted'>>;

type RegisterableModal<P> = React.FC<ModalRegistryManagedProps & P>;

/**
 * A map of modal name to modal component (typically wrapping a {@link WrappedModal}).
 *
 * @privateRemarks Type safety is ensured via {@link createModalRegistry}
 */
// oxlint-disable-next-line typescript/no-explicit-any
export type ModalsMap<P = any> = Record<string, RegisterableModal<P>>;

type OpenedModalProps<M extends ModalsMap, K extends keyof M & string> = Omit<
  React.ComponentProps<M[K]>,
  keyof ModalRegistryManagedProps
>;

/** `props` follows the same rules as `openModal`'s second argument. */
type TrackedModalOptions<M extends ModalsMap, K extends keyof M & string, T> = {
  /**
   * The modal is open while this is non-nil.
   *
   * This param should be referentially stable (state, a memoized value, a primitive): each new identity rebuilds the open
   * modal's props, so an object or array recreated every render re-renders the modal on every render.
   */
  variable: T | null | undefined;
  /** Called when the modal closes itself (dismissed or confirmed), but not when `variable` becomes nil. */
  onClose?: () => void;
} & (keyof OpenedModalProps<M, K> extends never
  ? { props?: never }
  : Record<never, never> extends OpenedModalProps<M, K>
    ? { props?: (variable: T) => OpenedModalProps<M, K> }
    : { props: (variable: T) => OpenedModalProps<M, K> });

type ModalRegistryContextType<M extends ModalsMap> = {
  /**
   * Open a Modal via Modal name.
   *
   * Returns a {@link ModalId} unique to the opened modal.
   * Can be used to close the modal programmatically, if needed.
   */
  openModal: <K extends keyof M & string>(
    // No second argument for modals without props of their own; optional when all props are optional
    ...args: keyof OpenedModalProps<M, K> extends never
      ? [modal: K]
      : Record<never, never> extends OpenedModalProps<M, K>
        ? [modal: K, props?: OpenedModalProps<M, K>]
        : [modal: K, props: OpenedModalProps<M, K>]
  ) => ModalId<K>;
  /**
   * Replaces the props of an open modal. Does nothing once the modal has closed.
   * @param modalId as returned from `openModal`
   */
  updateModal: <K extends keyof M & string>(modalId: ModalId<K>, props: OpenedModalProps<M, K>) => void;
  /** @param modalId as returned from `openModal` */
  closeModal: (modalId: ModalId) => void;
  /** Returns `true` if any Modals are currently opened in this registry. */
  anyModalOpened: boolean;
};

/** Used by the tracked-modal hook; not exposed to registry consumers. */
type InternalModalRegistryContextType<M extends ModalsMap> = ModalRegistryContextType<M> & {
  openTrackedModal: <K extends keyof M & string>(
    modalName: K,
    props: OpenedModalProps<M, K> | undefined,
    onClosed?: () => void,
  ) => ModalId<K>;
};

type OpenedModal = {
  modalId: ModalId;
  modalName: string;
  props?: Record<string, unknown>;
  /** `mounting` renders the modal closed for one tick so its enter transition can play */
  status: 'mounting' | 'opened' | 'closed';
  /** Created once per modal, so the registered component receives the same function on every render */
  onClose: () => void;
};

type ModalRegistryOptions = {
  /** Wraps the registry's open modals (not the provider's children), e.g. to give them their own color scheme. */
  ModalScope?: React.FC<{ children: ReactNode }>;
};

const ModalRegistryProvider = <M extends ModalsMap>({
  modals,
  ModalRegistryContext,
  ModalScope = React.Fragment,
  children,
}: {
  modals: M;
  ModalRegistryContext: React.Context<InternalModalRegistryContextType<M> | undefined>;
  children: ReactNode;
} & ModalRegistryOptions) => {
  const [modalsOpened, setModalsOpened] = useState<OpenedModal[]>([]);
  // A closed modal stays mounted through its exit transition and can still call `onClose`
  // (e.g. from a late request); those calls must not reach the opener's `onClosed`
  const liveModalIds = useRef(new Set<ModalId>());
  const { topModalId, push, remove } = useModalStack();

  useEffect(() => {
    const modalIds = liveModalIds.current;
    // Normally a no-op; restores this registry's modals if its effects were paused (e.g. inside a hidden `Activity`)
    modalIds.forEach(push);

    // Guarantee an unmounted registry removes all modals from the global stack
    return () => modalIds.forEach(remove);
  }, [push, remove]);

  const closeModal = useCallback<ModalRegistryContextType<M>['closeModal']>(
    (modalId) => {
      if (!liveModalIds.current.delete(modalId)) return;
      remove(modalId);
      // Close the modal...
      setModalsOpened((m) => m.map((modal) => (modal.modalId === modalId ? { ...modal, status: 'closed' } : modal)));
      // ...and then remove it entirely after it animates out (with buffer)
      setTimeout(() => {
        setModalsOpened((m) => m.filter((modal) => modal.modalId !== modalId));
      }, DEFAULT_TRANSITIONS_DURATION * 2);
    },
    [remove],
  );

  const openTrackedModal = useCallback<InternalModalRegistryContextType<M>['openTrackedModal']>(
    (modalName, props, onClosed) => {
      const modalId = uuid() as ModalId<typeof modalName>;
      liveModalIds.current.add(modalId);
      push(modalId);
      const onClose = () => {
        if (!liveModalIds.current.has(modalId)) return;
        closeModal(modalId);
        onClosed?.();
      };
      setModalsOpened((m) => [
        ...m,
        {
          modalId,
          modalName,
          props,
          // Mantine only animates `opened` changes *after* the first render, so a modal that
          // mounts already-opened pops into view. Mount it closed, then open it via the effect below.
          status: 'mounting',
          onClose,
        },
      ]);
      return modalId;
    },
    [closeModal, push],
  );

  const openModal = useCallback<ModalRegistryContextType<M>['openModal']>(
    (modalName, props?) => openTrackedModal(modalName, props),
    [openTrackedModal],
  );

  const updateModal = useCallback<ModalRegistryContextType<M>['updateModal']>((modalId, props) => {
    setModalsOpened((m) =>
      m.map((modal) => (modal.modalId === modalId && modal.status !== 'closed' ? { ...modal, props } : modal)),
    );
  }, []);

  useEffect(() => {
    if (!modalsOpened.some((modal) => modal.status === 'mounting')) return undefined;
    // Mantine's `Portal` renders its children only after a layout effect, and React runs this effect before that
    // re-render. Opening now would mount the modal's `Transition` already opened, so wait a frame for it to exist.
    const frame = requestAnimationFrame(() =>
      setModalsOpened((m) => m.map((modal) => (modal.status === 'mounting' ? { ...modal, status: 'opened' } : modal))),
    );
    return () => cancelAnimationFrame(frame);
  }, [modalsOpened]);

  const anyModalOpened = modalsOpened.some((modal) => modal.status !== 'closed');

  const value = useMemo(
    () => ({ openModal, closeModal, anyModalOpened, openTrackedModal, updateModal }),
    [openModal, closeModal, anyModalOpened, openTrackedModal, updateModal],
  );

  return (
    <ModalRegistryContext value={value}>
      <ModalScope>
        {modalsOpened.map(({ modalId, modalName, props, status, onClose }) => {
          const ModalComponent = modals[modalName];
          if (isNil(ModalComponent)) return null;
          return (
            <ModalComponent
              key={modalId}
              opened={status === 'opened' && modalId === topModalId}
              onClose={onClose}
              keepMounted={status !== 'closed'}
              {...props}
            />
          );
        })}
      </ModalScope>
      {children}
    </ModalRegistryContext>
  );
};

/** Each registered component's own props must include the registry-managed props */
type ValidatedModals<M extends ModalsMap> = {
  [K in keyof M]: React.ComponentProps<M[K]> extends ModalRegistryManagedProps
    ? M[K]
    : // If you are missing these required props,
      // you will get a tsc error when calling `createModalRegistry`
      ModalRegistryManagedProps;
};

/**
 * Used to create a Modal registry.
 *
 * @param modals A map of modal name to Modal component. Use object shorthand (`{ BendModal }`) so each name is its component's name.
 * @param options `ModalScope` wraps the open modals, e.g. `PageColorScheme` to keep them light on the dark stage.
 *
 * @returns a tuple of `[ModalRegistryProvider, useModalRegistry, useTrackedModal]`.
 *
 * @remarks Call this function in a colocated file called `modalRegistries.tsx`.
 * Then, use the provided `ModalRegistryProvider` to allow downstream components
 * to access your modals via `useModalRegistry`, or to keep a modal open while a variable is set
 * via `useTrackedModal`.
 *
 * @example
 * export const [FabricationModalsProvider, useFabricationModals, useFabricationTrackedModal] = createModalRegistry({
 *   BendModal,
 * });
 *
 * const FabricationPage = () => {
 *   ...
 *   return (
 *     <FabricationModalsProvider>
 *       {...}
 *       <DownstreamComponent />
 *     </FabricationModalsProvider>
 *   );
 * }
 *
 * const DownstreamComponent = () => {
 *   const { openModal } = useFabricationModals();
 *
 *   return (
 *     <Button
 *       onClick={() => openModal('BendModal', {...})}
 *     >
 *       Open Bend Modal
 *     </Button>
 *   )
 * }
 * */
export const createModalRegistry = <M extends ModalsMap>(
  modals: M & ValidatedModals<M>,
  { ModalScope }: ModalRegistryOptions = {},
) => {
  // Must define the context here, not at the file level; otherwise, each hook would find
  // the "nearest" Modal Registry instead of the correct one for this call of `createModalRegistry`
  const ModalRegistryContext = React.createContext<InternalModalRegistryContextType<M> | undefined>(undefined);
  const Provider = ({ children }: { children: ReactNode }) => (
    <ModalRegistryProvider modals={modals} ModalRegistryContext={ModalRegistryContext} ModalScope={ModalScope}>
      {children}
    </ModalRegistryProvider>
  );
  const useInternalModalRegistryContext = () => {
    const context = useContext(ModalRegistryContext);
    if (isNil(context)) {
      throw new Error(
        `Modal registry was accessed without the proper registry provider (${Object.keys(modals).join(', ')})`,
      );
    }
    return context;
  };
  const useModalRegistryContext = (): ModalRegistryContextType<M> => useInternalModalRegistryContext();

  /**
   * Keeps a modal open for as long as `variable` is non-nil, passing the non-nil value into `props`.
   *
   * - Changing `variable` to another non-nil value updates the open modal's props in place.
   * - Once the modal closes itself, it stays closed until `variable` becomes nil and then non-nil again.
   * - Unmounting the calling component closes the modal.
   */
  const useTrackedModal = <K extends keyof M & string, T extends NonNullable<unknown>>(
    modal: K,
    { variable, props, onClose }: TrackedModalOptions<M, K, T>,
  ) => {
    const { openTrackedModal, updateModal, closeModal } = useInternalModalRegistryContext();
    const tracked = isNotNil(variable);
    // The variable the open modal's props were last built from; nil once the modal closes itself
    const openedRef = useRef<{ modalId: ModalId<K>; variable: T } | null>(null);
    // The registry calls `onClosed` from an event handler long after it was created,
    // so read the latest `onClose` through a ref
    const onCloseRef = useRef(onClose);
    useEffect(() => {
      onCloseRef.current = onClose;
    }, [onClose]);

    const buildProps = (value: T) => props?.(value);

    const openTracked = useEffectEvent((modalName: K) => {
      if (isNil(variable)) return null;
      const modalId = openTrackedModal(modalName, buildProps(variable), () => {
        openedRef.current = null;
        onCloseRef.current?.();
      });
      openedRef.current = { modalId, variable };
      return modalId;
    });

    useEffect(() => {
      if (!tracked) return undefined;
      const modalId = openTracked(modal);
      if (isNil(modalId)) return undefined;
      return () => {
        openedRef.current = null;
        closeModal(modalId);
      };
    }, [tracked, modal, closeModal]);

    const syncProps = useEffectEvent((latestVariable: T) => {
      const opened = openedRef.current;
      if (isNil(opened) || opened.variable === latestVariable) return;
      openedRef.current = { ...opened, variable: latestVariable };
      const nextProps = buildProps(latestVariable);
      if (isNotNil(nextProps)) updateModal(opened.modalId, nextProps);
    });

    useEffect(() => {
      if (isNotNil(variable)) syncProps(variable);
    }, [variable]);
  };

  return [Provider, useModalRegistryContext, useTrackedModal] as const;
};
